import { useCallback, useEffect, useLayoutEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { fetchCdrs, fetchCdrStats, type CdrFilters, type CdrRecord, type CdrStats } from '../api/settlement';
import { ApiProblem } from '../api/client';

// CDR viewing (spec 2026-07-15) — the paginated records table. The section
// shell (filters, tiles, chart) composes this; it stays dumb: items in,
// load-more out.

export function CdrRecordsTable({ items, nextCursor, loading, onLoadMore, emptyText = 'No confirmation records match the current filters' }: {
  items: CdrRecord[];
  nextCursor: string | null;
  loading: boolean;
  onLoadMore: () => void;
  emptyText?: string;
}) {
  const { cases } = useAppData();
  const { openCase } = useUIState();

  return (
    <div>
      <table style={styles.table} data-testid="cdr-records-table">
        <thead><tr>
          <th style={styles.th}>Call Time</th>
          <th style={styles.th}>TSP</th>
          <th style={styles.th}>Orig</th>
          <th style={styles.th}>Dest</th>
          <th style={styles.th}>Claims</th>
          <th style={styles.th}>DIR ID</th>
          <th style={styles.th}>Case</th>
        </tr></thead>
        <tbody>
          {items.map((r, i) => {
            const linked = r.caseId ? cases.find((c) => c.id === r.caseId) : undefined;
            return (
              <tr key={`${r.callAt}-${r.tspid}-${i}`}>
                <td style={styles.td}>{fmtDateTime(r.callAt)}</td>
                <td style={styles.td}>{r.tspName ?? r.tspid}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{r.orig ?? '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{r.dest ?? '—'}</td>
                <td style={styles.td}>{r.claimsRequested ?? '—'} → {r.claimsPassed ?? '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{r.dirid ?? '—'}</td>
                <td style={styles.td}>
                  {linked ? (
                    <button
                      data-testid="cdr-case-link"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.goldText, fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}
                      onClick={() => openCase(linked)}
                    >{r.caseId}</button>
                  ) : (
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.textMuted }}>{r.caseId ?? '—'}</span>
                  )}
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr><td colSpan={7} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>
              {loading ? 'Loading…' : emptyText}
            </td></tr>
          )}
        </tbody>
      </table>
      {nextCursor && (
        <button
          data-testid="cdr-load-more"
          style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 10 }}
          disabled={loading}
          onClick={onLoadMore}
        >{loading ? 'Loading…' : 'Load more'}</button>
      )}
    </div>
  );
}

// Single-series bar chart, confirmations per UTC day. Inline SVG — one chart
// doesn't justify a dependency. Bars in C.goldText (≥3:1 on white); text in
// text tokens, never the series color; native <title> per bar as tooltip;
// selective direct label on the peak only.
function PerDayChart({ perDay }: { perDay: CdrStats['perDay'] }) {
  // The chart stretches to its container: days share the available width
  // (bars capped so they stay marks, not slabs), and only when the minimum
  // slot no longer fits does the wrapper's overflowX take over as a scroll.
  // A fixed px-per-day layout collapses a few-days chart into a sliver whose
  // two axis labels overlap (post-deploy finding, PR #175).
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(640); // nominal width until measured (jsdom stays 0)
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => { if (el.clientWidth > 0) setContainerW(el.clientWidth); };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (perDay.length === 0) return null;
  const maxCount = Math.max(...perDay.map((d) => d.count));
  const GAP = 2, PLOT_H = 120, LABEL_H = 16, TOP_PAD = 14, MIN_SLOT = 8, MAX_BAR = 40;
  const slotW = Math.max(MIN_SLOT, Math.floor(containerW / perDay.length));
  const width = slotW * perDay.length;
  const barW = Math.min(slotW - GAP, MAX_BAR);
  const xFor = (i: number) => i * slotW + (slotW - barW) / 2;
  const barH = (count: number) => Math.max(2, Math.round((count / maxCount) * (PLOT_H - TOP_PAD)));
  const peakIdx = perDay.findIndex((d) => d.count === maxCount);
  const peakX = Math.min(Math.max(xFor(peakIdx) + barW / 2, 8), width - 8);
  return (
    <div ref={wrapRef} style={{ overflowX: 'auto', marginTop: 8 }} data-testid="cdr-per-day-chart">
      <svg width={width} height={PLOT_H + LABEL_H} role="img" aria-label="Confirmations per day">
        {perDay.map((d, i) => (
          <rect key={d.day} x={xFor(i)} y={PLOT_H - barH(d.count)} width={barW} height={barH(d.count)} rx={2} fill={C.goldText}>
            <title>{`${d.day}: ${d.count}`}</title>
          </rect>
        ))}
        <text x={peakX} y={PLOT_H - barH(maxCount) - 4} textAnchor="middle" fontSize={10} fill={C.textMuted}>{maxCount}</text>
        <line x1={0} y1={PLOT_H} x2={width} y2={PLOT_H} stroke={C.border} strokeWidth={1} />
        <text x={0} y={PLOT_H + 12} fontSize={10} fill={C.textMuted}>{perDay[0].day}</text>
        {perDay.length > 1 && (
          <text x={width} y={PLOT_H + 12} textAnchor="end" fontSize={10} fill={C.textMuted}>{perDay[perDay.length - 1].day}</text>
        )}
      </svg>
    </div>
  );
}

interface FilterDraft { fromDate: string; toDate: string; tspid: string; caseOrEid: string; tn: string }
const EMPTY_DRAFT: FilterDraft = { fromDate: '', toDate: '', tspid: '', caseOrEid: '', tn: '' };

// #893 — a phone-number (tn) search matches the CALLED number too, which only
// the call-level records hold, and the API keeps those 90 days: it refuses a
// tn search whose From is empty or older (routes/settlement.ts /cdrs/stats).
// An empty From is therefore filled in, VISIBLY, 89 days back (one day inside
// the API's window, so its clock a moment later cannot cross it).
export const TN_SEARCH_DAYS = 89;
const DAY_MS = 86_400_000;

/** The From date a tn search defaults to: TN_SEARCH_DAYS before today (UTC). */
export function tnSearchDefaultFrom(now: Date = new Date()): string {
  return new Date(now.getTime() - TN_SEARCH_DAYS * DAY_MS).toISOString().slice(0, 10);
}

/** The draft as it is applied: a tn search with no From gets the default. */
function withTnDefault(d: FilterDraft): FilterDraft {
  return d.tn.trim() && !d.fromDate ? { ...d, fromDate: tnSearchDefaultFrom() } : d;
}

/** A failed request's message: the API's problem detail when it sent one. */
function problemText(err: unknown): string {
  if (err instanceof ApiProblem) return err.detail ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

// A 32-hex value is an EID; anything else is treated as a case id.
function draftToFilters(d: FilterDraft): CdrFilters {
  const f: CdrFilters = {};
  if (d.fromDate) f.from = `${d.fromDate}T00:00:00.000Z`;
  if (d.toDate) f.to = `${d.toDate}T23:59:59.999Z`;
  if (d.tspid) f.tspid = d.tspid;
  const ce = d.caseOrEid.trim();
  if (/^[0-9a-f]{32}$/i.test(ce)) f.eid = ce;
  else if (ce) f.caseId = ce;
  if (d.tn.trim()) f.tn = d.tn.trim();
  return f;
}

export function ConfirmationRecords() {
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<CdrFilters>({});
  const [stats, setStats] = useState<CdrStats | null>(null);
  const [items, setItems] = useState<CdrRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #893 — the stats can be refused (a tn search reaching past the 90-day
  // call-level window) while the list still answers, so the two fail apart.
  const [statsError, setStatsError] = useState<string | null>(null);
  // TSP dropdown options accumulate across loads so filtering to one TSP
  // doesn't collapse the list to itself.
  const [tspOptions, setTspOptions] = useState<Map<string, string>>(new Map());

  const load = useCallback((filters: CdrFilters) => {
    setLoading(true);
    setError(null);
    setStatsError(null);
    // allSettled, not all (#893): a refused stats request must not take the
    // record list down with it, and the reverse.
    Promise.allSettled([fetchCdrStats(filters), fetchCdrs(filters)])
      .then(([s, list]) => {
        if (s.status === 'fulfilled') {
          setStats(s.value);
          setTspOptions((prev) => {
            const next = new Map(prev);
            for (const t of s.value.perTsp) next.set(t.tspid, t.tspName ?? t.tspid);
            return next;
          });
        } else {
          setStats(null);
          setStatsError(problemText(s.reason));
        }
        if (list.status === 'fulfilled') {
          setItems(list.value.items);
          setNextCursor(list.value.nextCursor);
        } else {
          setError(problemText(list.reason));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(applied); }, [applied, load]);

  const loadMore = () => {
    if (!nextCursor) return;
    setLoading(true);
    fetchCdrs(applied, { before: nextCursor })
      .then((list) => {
        setItems((prev) => [...prev, ...list.items]);
        setNextCursor(list.nextCursor);
        setLoading(false);
      })
      .catch((err: unknown) => {
        // Only a stale/malformed cursor (400) resets to page one; let load()
        // own the loading flag on that path. Any other failure surfaces the
        // error without discarding the pages already accumulated.
        if (err instanceof ApiProblem && err.status === 400) {
          load(applied);
        } else {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });
  };

  const input = (testid: string, key: keyof FilterDraft, props: InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input
      data-testid={testid}
      style={{ ...styles.input, fontSize: 12 }}
      value={draft[key]}
      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      {...props}
    />
  );

  return (
    <div style={styles.card} data-testid="cdr-records-section">
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Confirmation Records</h3>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
        CTIA Confirmation Detail Records — every confirmed branded call ingested from the BCID Platform
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>From</label>{input('cdr-filter-from', 'fromDate', { type: 'date' })}</div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>To</label>{input('cdr-filter-to', 'toDate', { type: 'date' })}</div>
        <div>
          <label style={{ fontSize: 11, color: C.textMuted }}>TSP</label>
          <select
            data-testid="cdr-filter-tsp"
            style={{ ...styles.input, fontSize: 12 }}
            value={draft.tspid}
            onChange={(e) => setDraft((d) => ({ ...d, tspid: e.target.value }))}
          >
            <option value="">All TSPs</option>
            {[...tspOptions.entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>Case ID or EID</label>{input('cdr-filter-case', 'caseOrEid', { placeholder: 'C-123 or 32-char EID' })}</div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>Phone number</label>{input('cdr-filter-tn', 'tn', { placeholder: '+1440…  (exact)' })}</div>
        <button
          data-testid="cdr-apply-btn"
          style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
          disabled={loading}
          onClick={() => {
            const next = withTnDefault(draft);
            setDraft(next);
            setApplied(draftToFilters(next));
          }}
        >Apply</button>
      </div>
      {draft.tn.trim() !== '' && (
        <div data-testid="cdr-tn-window-note" style={{ fontSize: 11, color: C.textMuted, marginTop: -8, marginBottom: 12 }}>
          Phone-number searches read call-level records, which are kept 90 days. With no From date,
          the search starts {TN_SEARCH_DAYS} days back.
        </div>
      )}

      {error && (
        <div data-testid="cdr-error" style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>
          Failed to load confirmation records: {error}{' '}
          <button data-testid="cdr-retry" style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginLeft: 8 }} onClick={() => load(applied)}>Retry</button>
        </div>
      )}

      {statsError && (
        <div data-testid="cdr-stats-error" style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>
          Totals unavailable for these filters: {statsError}
        </div>
      )}

      {stats && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.goldText}` }} data-testid="cdr-tile-total">
              <div style={{ fontSize: 24, fontWeight: 700, color: C.goldText }}>{stats.total}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>Confirmed calls</div>
            </div>
            <div style={styles.statBox}>
              <div style={{ fontSize: 24, fontWeight: 700, color: C.text }}>{stats.perTsp.length}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>Carriers (TSPs)</div>
            </div>
            {stats.perTsp[0] && (
              <div style={styles.statBox}>
                <div style={{ fontSize: 24, fontWeight: 700, color: C.text }}>{stats.perTsp[0].tspName ?? stats.perTsp[0].tspid}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>Top carrier ({stats.perTsp[0].count})</div>
              </div>
            )}
          </div>
          <PerDayChart perDay={stats.perDay} />
          {stats.perTsp.length > 0 && (
            <table style={{ ...styles.table, maxWidth: 420, marginTop: 12, marginBottom: 16 }} data-testid="cdr-tsp-breakdown">
              <thead><tr><th style={styles.th}>TSP</th><th style={styles.th}>Count</th><th style={styles.th}>Share</th></tr></thead>
              <tbody>
                {stats.perTsp.map((t) => (
                  <tr key={t.tspid}>
                    <td style={styles.td}>{t.tspName ?? t.tspid}</td>
                    <td style={styles.td}>{t.count}</td>
                    <td style={styles.td}>{stats.total > 0 ? Math.round((t.count / stats.total) * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <CdrRecordsTable
        items={items}
        nextCursor={nextCursor}
        loading={loading}
        onLoadMore={loadMore}
        // Spec: zero rows with no filters keeps the "not confirmed yet" tone.
        emptyText={Object.keys(applied).length > 0
          ? 'No confirmation records match the current filters'
          : 'No branded calls have been confirmed on a handset yet.'}
      />
    </div>
  );
}
